/// <amd-module name="Artifacts/Feed/redux/Actions" />
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { IArtifactsSettingsData } from "Artifacts/Feed/services/ArtifactsSettingsService";
import { ICreateFeedOptions } from "Artifacts/Feed/services/CreateFeedService";
import { PackageListDataProviderParams } from "Artifacts/Feed/services/PackageListService";
import { ProblemPackageItem } from "Artifacts/Feed/types/ProblemPackageItem";
import { ILoadFailedPayload } from "Artifacts/Root/redux";
import { IFeed, IFeedIdentity, IPackageVersionIdentity } from "Artifacts/Root/types";
import {
  CreateFeedAdditionalData,
  PackageListDataItem,
  ViewIdentifier,
} from "Artifacts/Root/types/AzureArtifacts";
import {
  ActionsUnion,
  createAction,
  trackedAction,
  trackedErrorAction,
} from "redux/Utils/ActionHelper";

export const enum FeedHubActionTypes {
  LoadPackageList = "Artifacts/Feed/LoadPackageList",
  LoadPackageListStarted = "Artifacts/Feed/LoadPackageListStarted",
  LoadPackageListCompleted = "Artifacts/Feed/LoadPackageListCompleted",
  PackageListAmendPackages = "Artifacts/Feed/PackageListAmendPackages",
  LoadPackageListFailed = "Artifacts/Feed/LoadPackageListFailed",

  LoadProblemPackageList = "Artifacts/Feed/LoadProblemPackageList",
  LoadProblemPackageListStarted = "Artifacts/Feed/LoadProblemPackageListStarted",
  LoadProblemPackageListCompleted = "Artifacts/Feed/LoadProblemPackageListCompleted",
  LoadProblemPackageListFailed = "Artifacts/Feed/LoadProblemPackageListFailed",

  // Command Action fakes
  FakePackagePromote = "Artifacts/Feed/FakePackagePromote",
  FakePackageDelete = "Artifacts/Feed/FakePackageDelete",
  FakePackageListUnlist = "Artifacts/Feed/FakePackageListUnlist",
}

export type FetchType = "initial" | "amend" | "filtering";
export interface IPackageListLoadPayload {
  feed: IFeed;
  view?: ViewIdentifier;
  params?: PackageListDataProviderParams;
  fetchType: FetchType;
}

export interface IProblemPackageListLoadPayload {
  feed: IFeed;
}

export interface IPackageListLoadCompletedPayload {
  packageData: PackageListDataItem[];
}

export interface IProblemPackageListLoadCompletedPayload {
  packageData: ProblemPackageItem[];
}

export interface IFakePromotePackagePayload {
  selectedVersions: IPackageVersionIdentity[];
  viewName: string;
}

export interface IFakeDeletePackagePayload {
  selectedVersions: IPackageVersionIdentity[];
}

export interface IFakeListUnlistPackagePayload {
  selectedVersions: IPackageVersionIdentity[];
  isListed: boolean;
}

export const FeedHubActions = {
  loadPackageList: (
    feed: IFeed,
    view?: ViewIdentifier,
    params?: PackageListDataProviderParams,
    fetchType: FetchType = "initial",
  ) =>
    createAction<FeedHubActionTypes, IPackageListLoadPayload>(FeedHubActionTypes.LoadPackageList, {
      feed,
      view,
      params,
      fetchType,
    }),
  loadPackageListStarted: () => createAction(FeedHubActionTypes.LoadPackageListStarted),
  loadPackageListCompleted: (packageData: PackageListDataItem[]) =>
    createAction<FeedHubActionTypes, IPackageListLoadCompletedPayload>(
      FeedHubActionTypes.LoadPackageListCompleted,
      { packageData },
    ),
  packageListAmendPackages: (packageData: PackageListDataItem[]) =>
    createAction<FeedHubActionTypes, IPackageListLoadCompletedPayload>(
      FeedHubActionTypes.PackageListAmendPackages,
      { packageData },
    ),
  loadPackageListFailed: (error: string) =>
    createAction<FeedHubActionTypes, ILoadFailedPayload>(FeedHubActionTypes.LoadPackageListFailed, {
      error,
    }),
  loadProblemPackageList: (feed: IFeed) =>
    createAction<FeedHubActionTypes, IProblemPackageListLoadPayload>(
      FeedHubActionTypes.LoadProblemPackageList,
      {
        feed,
      },
    ),
  loadProblemPackageListStarted: () =>
    createAction(FeedHubActionTypes.LoadProblemPackageListStarted),
  loadProblemPackageListCompleted: (failedPackageData: ProblemPackageItem[]) =>
    createAction<FeedHubActionTypes, IProblemPackageListLoadCompletedPayload>(
      FeedHubActionTypes.LoadProblemPackageListCompleted,
      { packageData: failedPackageData },
    ),
  loadProblemPackageListFailed: (error: string) =>
    createAction<FeedHubActionTypes, ILoadFailedPayload>(
      FeedHubActionTypes.LoadProblemPackageListFailed,
      {
        error,
      },
    ),
  fakePackagePromote: (selectedVersions: IPackageVersionIdentity[], viewName: string) =>
    createAction<FeedHubActionTypes, IFakePromotePackagePayload>(
      FeedHubActionTypes.FakePackagePromote,
      {
        selectedVersions,
        viewName,
      },
    ),
  fakePackageDelete: (selectedVersions: IPackageVersionIdentity[]) =>
    createAction<FeedHubActionTypes, IFakeDeletePackagePayload>(
      FeedHubActionTypes.FakePackageDelete,
      {
        selectedVersions,
      },
    ),
  fakePackageListUnlist: (selectedVersions: IPackageVersionIdentity[], isListed: boolean) =>
    createAction<FeedHubActionTypes, IFakeListUnlistPackagePayload>(
      FeedHubActionTypes.FakePackageListUnlist,
      {
        selectedVersions,
        isListed,
      },
    ),
};

export type FeedHubActions = ActionsUnion<typeof FeedHubActions>;

export const enum CreateFeedActionTypes {
  // Get parameters from server for feed creation
  LoadCreateFeedParams = "/Artifacts/Feed/LoadCreateFeedParams",
  LoadCreateFeedParamsStarted = "/Artifacts/Feed/LoadCreateFeedParamsStarted",
  LoadCreateFeedParamsCompleted = "/Artifacts/Feed/LoadCreateFeedParamsCompleted",
  LoadCreateFeedParamsFailed = "/Artifacts/Feed/LoadCreateFeedParamsFailed",

  // Create feed w/ write APIs
  CreateFeed = "Artifacts/Feed/CreateFeed",
  CreateFeedStarted = "Artifacts/Feed/CreateFeedStarted",
  CreateFeedCompleted = "Artifacts/Feed/CreateFeedCompleted",
  CreateFeedFailed = "Artifacts/Feed/CreateFeedFailed",
}

export interface ICreateFeedPayload {
  createFeedData: ICreateFeedOptions;
  feedServiceParams: CreateFeedAdditionalData;
  // This is for redirecting to new feed on successful creation
  handleCreatedFeed: (feedIdentifier: string) => void;
}

export interface ILoadCreateFeedParamsPayload {
  createFeedServiceParams: CreateFeedAdditionalData;
}

export const CreateFeedActions = {
  createFeed: (
    createFeedData: ICreateFeedOptions,
    feedServiceParams: CreateFeedAdditionalData,
    handleCreatedFeed: (feedIdentifier: string) => void,
  ) =>
    trackedAction(
      createAction<CreateFeedActionTypes, ICreateFeedPayload>(CreateFeedActionTypes.CreateFeed, {
        createFeedData,
        feedServiceParams,
        handleCreatedFeed,
      }),
    ),
  createFeedStarted: () => createAction(CreateFeedActionTypes.CreateFeedStarted),
  createFeedCompleted: (createdFeedData: IFeedIdentity) =>
    trackedAction(
      createAction<CreateFeedActionTypes, IFeedIdentity>(
        CreateFeedActionTypes.CreateFeedCompleted,
        createdFeedData,
      ),
    ),
  createFeedFailed: (error: string) =>
    trackedErrorAction(
      createAction<CreateFeedActionTypes, ILoadFailedPayload>(
        CreateFeedActionTypes.CreateFeedFailed,
        {
          error,
        },
      ),
    ),
  loadCreateFeedParams: () => createAction(CreateFeedActionTypes.LoadCreateFeedParams),
  loadCreateFeedparamsStarted: () =>
    createAction(CreateFeedActionTypes.LoadCreateFeedParamsStarted),
  loadCreateFeedParamsCompleted: (createFeedServiceParams: CreateFeedAdditionalData) =>
    createAction<CreateFeedActionTypes, ILoadCreateFeedParamsPayload>(
      CreateFeedActionTypes.LoadCreateFeedParamsCompleted,
      { createFeedServiceParams },
    ),
  loadCreateFeedParamsFailed: (error: string) =>
    createAction<CreateFeedActionTypes, ILoadFailedPayload>(
      CreateFeedActionTypes.LoadCreateFeedParamsFailed,
      {
        error,
      },
    ),
};

export type CreateFeedActions = ActionsUnion<typeof CreateFeedActions>;

export const enum ArtifactsSettingsActionTypes {
  SaveArtifactsSettings = "/Artifacts/Feed/SaveArtifactsSettings",
  SaveArtifactsSettingsStarted = "/Artifacts/Feed/SaveArtifactsSettingsStarted",
  SaveArtifactsSettingsCompleted = "/Artifacts/Feed/SaveArtifactsSettingsCompleted",
  SaveArtifactsSettingsFailed = "/Artifacts/Feed/SaveArtifactsSettingsFailed",
  LoadArtifactsSettingsParams = "/Artifacts/Feed/LoadArtifactsSettingsParams",
  LoadArtifactsSettingsParamsStarted = "/Artifacts/Feed/LoadArtifactsSettingsParamsStarted",
  LoadArtifactsSettingsParamsFailed = "/Artifacts/Feed/LoadArtifactsSettingsParamsFailed",
  LoadArtifactsSettingsParamsCompleted = "/Artifacts/Feed/LoadArtifactsSettingsParamsCompleted",
}

export interface IArtifactsSettingsPayload {
  artifactsSettingsData: IArtifactsSettingsData;
  feed: IFeed;
  handleSaveSuccessful: () => void;
}

export interface ILoadArtifactsSettingsPayload {
  feed: IFeed | undefined;
}

export const ArtifactsSettingsActions = {
  saveArtifactsSettings: (
    artifactsSettingsData: IArtifactsSettingsData,
    feed: IFeed,
    handleSaveSuccessful: () => void,
  ) =>
    trackedAction(
      createAction<ArtifactsSettingsActionTypes, IArtifactsSettingsPayload>(
        ArtifactsSettingsActionTypes.SaveArtifactsSettings,
        {
          artifactsSettingsData,
          feed,
          handleSaveSuccessful,
        },
      ),
    ),
  saveArtifactsSettingsStarted: () =>
    createAction(ArtifactsSettingsActionTypes.SaveArtifactsSettingsStarted),
  saveArtifactsSettingsCompleted: () =>
    trackedAction(createAction(ArtifactsSettingsActionTypes.SaveArtifactsSettingsCompleted)),
  saveArtifactsSettingsFailed: (error: string) =>
    trackedErrorAction(
      createAction<ArtifactsSettingsActionTypes, ILoadFailedPayload>(
        ArtifactsSettingsActionTypes.SaveArtifactsSettingsFailed,
        {
          error,
        },
      ),
    ),
  loadArtifactsSettingsParams: (feed: IFeed | undefined) =>
    createAction<ArtifactsSettingsActionTypes, ILoadArtifactsSettingsPayload>(
      ArtifactsSettingsActionTypes.LoadArtifactsSettingsParams,
      { feed },
    ),
  loadArtifactsSettingsStarted: () =>
    createAction(ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsStarted),
  loadArtifactsSettingsCompleted: (state: IArtifactsSettingsData | undefined) =>
    createAction(ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsCompleted, state),
  loadArtifactsSettingsFailed: (error: string) =>
    createAction<ArtifactsSettingsActionTypes, ILoadFailedPayload>(
      ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsFailed,
      {
        error,
      },
    ),
};

export type ArtifactsSettingsActions = ActionsUnion<typeof ArtifactsSettingsActions>;
